import type { Tool } from './types';

export const dashaCalculator: Tool = {
  slug: 'dasha-calculator',
  metaTitle: 'Dasha Calculator — Vimshottari Mahadasha & Antardasha Periods',
  metaDescription:
    'Free Vimshottari dasha calculator. See your current mahadasha and antardasha, full 120-year dasha timeline by date of birth, and an AI explanation of what each period means for you.',
  keywords:
    'dasha calculator, vimshottari dasha calculator, mahadasha calculator, antardasha calculator, current dasha, dasha period by date of birth, mahadasha antardasha, dasha timeline',
  eyebrow: 'Planetary Periods',
  h1: 'Vimshottari Dasha Calculator (Mahadasha & Antardasha)',
  appName: 'KundaliAI Dasha Calculator',
  calculator: 'dasha',
  intro:
    'See which planetary period you are running right now. KundaliAI computes your full Vimshottari dasha timeline — mahadasha, antardasha and start/end dates — from your date, time and place of birth, then explains what your current period typically brings for career, relationships and health, based on how that planet actually sits in your chart. Free to start.',
  ctaLabel: 'Check my current Dasha free',
  ctaHref: '/chat',
  sections: [
    {
      heading: 'What is Vimshottari dasha?',
      body: 'Vimshottari is the most widely used timing system in Vedic astrology: a **120-year cycle** of nine planetary periods (mahadashas) — Ketu 7y, Venus 20y, Sun 6y, Moon 10y, Mars 7y, Rahu 18y, Jupiter 16y, Saturn 19y, Mercury 17y. Each mahadasha is subdivided into **antardashas** (sub-periods) of all nine planets in sequence. Jyotishis use dashas to judge *when* the promises of a birth chart are likely to unfold.',
    },
    {
      heading: 'How the calculator works',
      body: "Your dasha sequence starts from your **janma nakshatra**: the nakshatra's ruling planet gives the first mahadasha, and the Moon's exact progress through that nakshatra sets how much of it remains at birth. KundaliAI computes the Moon's sidereal longitude (Lahiri ayanamsa) with astronomical-grade ephemeris data, so the balance-of-dasha — and every start and end date after it — lands where a careful hand calculation would put it. Even a few minutes of birth-time error can shift dasha dates, so use your recorded birth time.",
    },
    {
      heading: 'Reading your dasha periods',
      body: 'A dasha is not good or bad by itself — it activates whatever that planet **means in your chart**: the houses it rules, occupies and aspects, and its strength. A Saturn mahadasha with a well-placed Saturn can be a decade of steady building; the same label with an afflicted Saturn reads very differently. KundaliAI interprets your current mahadasha–antardasha combination against your actual chart rather than giving one-size-fits-all predictions, and you can ask follow-up questions about specific years.',
    },
  ],
  faq: [
    {
      question: 'How do I know my current dasha?',
      answer:
        'Enter your date, time and place of birth. The calculator derives your starting dasha from your janma nakshatra and the Moon\'s exact position, then rolls the 120-year Vimshottari sequence forward to today to show your current mahadasha and antardasha with dates.',
    },
    {
      question: 'What is the difference between mahadasha and antardasha?',
      answer:
        'A mahadasha is the major period ruled by one planet (6 to 20 years). Within it, each of the nine planets rules a shorter antardasha in proportion to its own mahadasha length. Predictions are usually read from the mahadasha and antardasha lords together.',
    },
    {
      question: 'Why do different sites show slightly different dasha dates?',
      answer:
        'Dasha dates depend on the Moon\'s computed longitude and the ayanamsa used. Sites using coarser ephemerides or a different ayanamsa can drift by days or weeks. KundaliAI uses VSOP87-grade positions with the Lahiri ayanamsa, the common Indian standard.',
    },
    {
      question: 'Which dasha is good for marriage or career?',
      answer:
        'It depends on your chart: periods of planets connected to the 7th house (marriage) or 10th house (career) tend to activate those areas. Ask the AI astrologer to check which of your upcoming antardashas touch those houses in your kundali.',
    },
  ],
  related: [
    { label: 'Nakshatra calculator (birth star)', href: '/nakshatra-calculator' },
    { label: 'Free Kundli by date of birth', href: '/free-kundli' },
    { label: 'Talk to an AI astrologer', href: '/ai-astrologer' },
  ],
};
